import { cnpj, cpf } from 'cpf-cnpj-validator'
import { Injectable, QueryList } from '@angular/core'
import { AbstractControl, FormControl, Validators } from '@angular/forms'
import { FormComponent } from '../app/shared/components/form/form.component'
import { IChoice, IQuestion } from '../interfaces/question/question.interface'

@Injectable({
  providedIn: 'root',
})
export class FormHelper {
  setFieldChoices(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    choices: IChoice[],
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const foundQuestion = this.getQuestion(foundForm, fieldKey)
    if (foundQuestion) {
      foundQuestion.choices = choices
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  patchAllFormFields(
    formComponent: QueryList<FormComponent>,
    fieldValues: { [key: string]: string | number | boolean | Date | number[] },
  ): void {
    for (const form of formComponent.toArray()) {
      if (form) {
        this.patchFormFields(formComponent, form.key, fieldValues)
      }
    }
  }

  patchFormFields(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldValues: { [key: string]: string | number | boolean | Date | number[] },
  ): void {
    for (const [key, value] of Object.entries(fieldValues)) {
      this.patchFieldValue(formComponent, formKey, key, value)
    }
  }

  clearAllFormFields(
    formComponent: QueryList<FormComponent>,
    formKey: string,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    if (foundForm) {
      foundForm.form.reset()
    } else {
      console.warn(`[FormHelper] Form "${formKey}" not found`)
    }
  }

  resetAllForms(formComponent: QueryList<FormComponent>): void {
    for (const form of formComponent.toArray()) {
      if (form) {
        form.form.reset()
      } else {
        console.warn(`[FormHelper] Form "${form.key}" not found`)
      }
    }
  }

  toggleAllFieldsRequirement(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    isRequired: boolean,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    if (foundForm) {
      for (const question of foundForm.questions) {
        if (question.required !== isRequired) {
          question.required = isRequired
          this.setFieldRequirement(
            formComponent,
            formKey,
            question.key,
            isRequired,
          )
        }
      }
    } else {
      console.warn(`[FormHelper] Form "${formKey}" not found`)
    }
  }

  patchFieldValue(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    fieldValue: string | number | boolean | Date | number[],
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      if (field.value !== fieldValue) {
        field.setValue(fieldValue)
      } else {
        console.warn(
          `[FormHelper] Field "${fieldKey}" already has the same value`,
        )
      }
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  getField(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): AbstractControl | null {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      return field
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
      return null
    }
  }

  getFields(
    formComponent: QueryList<FormComponent>,
    formKey: string,
  ): { [key: string]: AbstractControl } | null {
    const foundForm = this.getForm(formComponent, formKey)
    const fields = foundForm?.form?.controls
    if (fields) {
      return fields
    } else {
      console.warn(`[FormHelper] Form "${formKey}" not found`)
      return null
    }
  }

  toggleFieldVisibility(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    visible: boolean,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const foundQuestion = this.getQuestion(foundForm, fieldKey)
    if (foundQuestion) {
      foundQuestion.isVisible = visible
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  toggleFieldStatus(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    status: boolean,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      if (field.enabled !== status) {
        if (status) {
          field.enable()
        } else {
          field.disable()
        }
      } else {
        console.warn('[FormHelper] Field already has the same status')
      }
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  setFieldRequirement(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    status: boolean,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    const foundQuestion = this.getQuestion(foundForm, fieldKey)
    if (field) {
      if (status) {
        field.setValidators([Validators.required])
        if (foundQuestion) {
          foundQuestion.required = true
        }
      } else {
        field.clearValidators()
        if (foundQuestion) {
          foundQuestion.required = false
        }
      }
      field.updateValueAndValidity()
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  setCustomFieldError(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    message: string,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      field.setErrors({
        incorrect: true,
        message,
      })
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  setFieldAsValid(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      field.setErrors(null)
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  setFieldAsInvalid(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      field.setErrors({
        incorrect: true,
      })
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  toggleFieldTouched(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
    touched: boolean,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      if (touched) {
        field.markAsTouched()
      } else {
        field.markAsUntouched()
      }
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
    }
  }

  getNotFilledRequiredFields(
    formComponent: QueryList<FormComponent>,
  ): string[] {
    const notFilledRequiredFields: Array<IQuestion[]> = []
    for (const form of formComponent.toArray()) {
      const invalidFields = form.questions.filter(
        (question) =>
          question.required &&
          !form.form.get(question.key)?.valid &&
          !form.form.get(question.key)?.disabled,
      )
      notFilledRequiredFields.push(invalidFields)
    }
    notFilledRequiredFields.flat().forEach((question) => {
      this.toggleFieldTouched(
        formComponent,
        question.formKey || '',
        question.key,
        true,
      )
    })
    const notFilledQuestionsTitles = notFilledRequiredFields
      .flat()
      .map((question) => question.title)
    return notFilledQuestionsTitles
  }

  validateCpf(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): boolean {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      const isCpfValid = cpf.isValid(field.value)
      if (!isCpfValid) {
        this.setCustomFieldError(
          formComponent,
          formKey,
          fieldKey,
          'CPF inválido',
        )
      }
      return isCpfValid
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
      return false
    }
  }

  verifyLink(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): boolean {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      const regex = /^(ftp|http|https):\/\/[^ "]+$/
      const isLink = regex.test(field.value)
      if (!isLink) {
        this.setCustomFieldError(
          formComponent,
          formKey,
          fieldKey,
          'Link inválido',
        )
      }
      return isLink
    }
  }

  validateIdentification(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): boolean {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      const isIdentificationValid =
        field?.value?.length >= 14
          ? cnpj.isValid(field?.value)
          : cpf.isValid(field?.value)
      if (!isIdentificationValid) {
        this.setCustomFieldError(
          formComponent,
          formKey,
          fieldKey,
          'CPF/CNPJ inválido',
        )
      }
      return isIdentificationValid
    } else {
      console.warn(`[FormHelper] Field "${fieldKey}" not found`)
      return false
    }
  }

  validateCNPJ(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    fieldKey: string,
  ): boolean {
    const foundForm = this.getForm(formComponent, formKey)
    const field = foundForm?.form?.get(fieldKey)
    if (field) {
      const isIdentificationValid =
        field?.value?.length >= 14 ? cnpj.isValid(field?.value) : false
      if (!isIdentificationValid) {
        this.setCustomFieldError(
          formComponent,
          formKey,
          fieldKey,
          'CNPJ inválido',
        )
      }
      return isIdentificationValid
    } else {
      console.warn(`[FormHelper] Field CNPJ not found`)
      return false
    }
  }

  getQuestion(
    foundForm: FormComponent,
    fieldKey: string,
  ): IQuestion | undefined {
    return foundForm.questions.filter(
      (question) => question.key === fieldKey,
    )[0]
  }

  getAllFormValues(formComponent: QueryList<FormComponent>): {
    [key: string]: {
      [key: string]: string | number | boolean | Date | number[]
    }
  } {
    const formValues: {
      [key: string]: {
        [key: string]: string | number | boolean | Date | number[]
      }
    } = {}
    for (const form of formComponent.toArray()) {
      formValues[form.key] = form.form.value
    }
    return formValues
  }

  addNewRow(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    keys: string[],
    customProperty?: any,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const questions = foundForm.questions
    const matchQuestionKeys = questions.filter((question) =>
      keys.includes(question.key.split('-')[0]),
    )
    if (matchQuestionKeys.length > keys.length) {
      matchQuestionKeys.splice(0, matchQuestionKeys.length - keys.length)
    }
    const nextRowNumber = Number(matchQuestionKeys[0].key.split('-')[1]) + 1
    for (const question of matchQuestionKeys) {
      const newKey = question.key.split('-')[0] + '-' + nextRowNumber
      let newQuestion = { ...question, key: newKey }
      const questionIndex = matchQuestionKeys.indexOf(question)
      if (customProperty && questionIndex === matchQuestionKeys.length - 1) {
        newQuestion = { ...newQuestion, ...customProperty }
      }
      foundForm.questions.push(newQuestion)
      const options = {
        validators: question.required
          ? [Validators.required]
          : [Validators.nullValidator],
      }
      foundForm.form.addControl(newKey, new FormControl())
      foundForm.form.get(newKey)?.setValidators(options.validators)
    }
    console.log(foundForm.form)
  }

  removeRow(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    keys: string[],
    rowNumber: number,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const questions = foundForm.questions
    const matchQuestionKeys = questions.filter((question) =>
      keys.includes(question.key.split('-')[0]),
    )
    for (const question of matchQuestionKeys) {
      const keyToRemove = question.key.split('-')[0] + '-' + rowNumber
      foundForm.questions = foundForm.questions.filter(
        (question) => question.key !== keyToRemove,
      )
      foundForm.form.removeControl(keyToRemove)
    }
  }

  patchRows(
    formComponent: QueryList<FormComponent>,
    formKey: string,
    rows: any[],
    keys: string[],
    customProperty?: any,
  ): void {
    const foundForm = this.getForm(formComponent, formKey)
    const questions = foundForm.questions
    rows.forEach((row, index) => {
      Object.keys(row).forEach((key) => {
        if (keys.includes(key)) {
          if (key === keys[0]) {
            const question = questions.filter(
              (question) => question.key === key + '-' + index + 1,
            )[0]
            if (question) {
              const field = foundForm.form.get(key + '-' + index + 1)
              if (field) {
                field.patchValue(row[key])
                question.value = row[key]
              }
            } else {
              this.addNewRow(formComponent, formKey, keys, customProperty)
            }
          }
          this.patchFieldValue(
            formComponent,
            formKey,
            key + '-' + (index + 1),
            row[key],
          )
        }
      })
    })
    // Fix to remove extra row
    const lastRowNumber = rows.length + 1
    this.removeRow(formComponent, formKey, keys, lastRowNumber)
  }

  getForm(
    formComponent: QueryList<FormComponent>,
    formKey: string,
  ): FormComponent {
    return formComponent.toArray().filter((form) => form.key === formKey)[0]
  }
}
